// Grupos de WhatsApp por QR (Masifi F3a). La lógica frontend de grupos vive acá y en componentes net-new; los archivos
// core solo importan (touchpoints de import + 1 línea). Spec: /root/docs/whatsapp-qr-f3-groups-design.md §6.1.
import { WHATSAPP_QR_FEATURE } from './masifiWhatsappQr';

export const MASIFI_WA_KIND_SETTING = 'masifi_wa_conversation_kind';
export const MASIFI_WA_KINDS = ['all', 'individual', 'group'];
const ACTIVITY = 2;

// Solo presentación: el servidor decide con masifi_wa_groups (ruling 23).
export const isMasifiWaGroupContact = contact =>
  contact?.additional_attributes?.masifi_wa_group === true ||
  contact?.additionalAttributes?.masifiWaGroup === true;

export const isMasifiWaGroupChat = chat =>
  isMasifiWaGroupContact(chat?.meta?.sender);

export const isMasifiWaGroupMessage = contentAttributes =>
  String(
    contentAttributes?.masifiWaChat || contentAttributes?.masifi_wa_chat || ''
  ).startsWith('group:');

// Paleta determinística por autor = colores del Button nativo (sin clases propias, regla 6).
const AUTHOR_COLORS = ['blue', 'ruby', 'amber', 'teal'];
export const masifiWaAuthorColor = key => {
  const hash = [...String(key ?? '')].reduce(
    (acc, char) => (acc * 31 + char.codePointAt(0)) % 1000003,
    7
  );
  return AUTHOR_COLORS[hash % AUTHOR_COLORS.length];
};

const typeOf = message => message?.message_type ?? message?.messageType;
const authorOf = message => {
  const attrs = message?.content_attributes || message?.contentAttributes || {};
  const author = attrs.masifi_wa_author || attrs.masifiWaAuthor;
  if (author) return author.lid || author.phone || null;
  return message?.sender_id ?? message?.senderId ?? message?.sender?.id ?? null;
};

// ¿Primer mensaje de una racha del mismo autor? Una sola pasada por lista (caché por arreglo + firma): cada burbuja lo
// pregunta y recorrer la lista por burbuja sería cuadrático (regla 8). Fuera de la lista (búsqueda, vista previa) → sí.
const runCache = new WeakMap();
export const isMasifiWaRunStart = (messages, messageId) => {
  if (!Array.isArray(messages) || !messages.length) return true;
  const signature = `${messages.length}:${messages[messages.length - 1]?.id}`;
  let entry = runCache.get(messages);
  if (!entry || entry.signature !== signature) {
    const starts = new Set();
    const ids = new Set();
    messages.reduce((previous, message) => {
      const key = `${typeOf(message)}:${authorOf(message)}`;
      if (key !== previous) starts.add(message.id);
      ids.add(message.id);
      return key;
    }, null);
    entry = { signature, starts, ids };
    runCache.set(messages, entry);
  }
  return !entry.ids.has(messageId) || entry.starts.has(messageId);
};

// ¿Cambia el autor entre dos mensajes seguidos de un GRUPO? (E2E fix batch 6, m1). Touchpoint: el shouldGroupWithNext
// nativo de MessageList.vue compara el sender, y en un grupo los ecos no tienen sender (nuestro teléfono y cada
// compañero "Es de mi equipo") → juntaba en una racha a autores distintos y el avatar del último quedaba debajo de
// todos (el mensaje del owner bajo la foto del compañero). Misma clave de autor que isMasifiWaRunStart (etiqueta del
// nombre). true = cortar la racha; false = regla nativa (1:1, no-QR, mismo autor).
const isGroupRow = message =>
  isMasifiWaGroupMessage(
    message?.contentAttributes || message?.content_attributes
  );
export const masifiWaAuthorChanged = (current, next) =>
  (isGroupRow(current) || isGroupRow(next)) &&
  authorOf(current) !== authorOf(next);

// Avatar del autor de un mensaje ENTRANTE de grupo (E2E fix 4, Marcos 2026-09-24): el mismo slot nativo de 24 px que
// usa el agente, a la izquierda (espejo del layout RIGHT de Message.vue) y con la misma regla nativa de colocación
// (último mensaje de la racha: groupWithNext compara el sender = contacto del participante). El nombre sigue dentro de
// la burbuja (MasifiWaGroupAuthor, ruling 1). Foto = thumbnail del contacto; sin foto → iniciales del mismo nombre que
// usa la burbuja. null = layout nativo (1:1, no-QR, salientes incluidos los ecos de "Equipo", actividad).
const INCOMING = 0;
const GROUP_INCOMING_GRID = {
  gridClass: 'grid grid-cols-[24px_1fr]',
  gridTemplate: `
      "avatar bubble"
      "spacer meta"
    `,
};
export const masifiWaGroupIncomingLayout = message => {
  if (typeOf(message) !== INCOMING) return null;
  const attrs = message?.contentAttributes || message?.content_attributes;
  if (!isMasifiWaGroupMessage(attrs)) return null;
  const author = attrs.masifiWaAuthor || attrs.masifi_wa_author;
  const { sender } = message;
  return {
    ...GROUP_INCOMING_GRID,
    avatar: {
      name: sender?.name || author?.name || '',
      src: sender?.thumbnail || '',
    },
  };
};

// Avatar de lo que escribe un compañero "Es de mi equipo" en un grupo (E2E fix 5, Marcos 2026-09-24). La fila es un eco
// SALIENTE sin sender (G10): el avatar nativo del eco es el del inbox (ícono genérico de WhatsApp). Acá va la foto del
// contacto del compañero (masifi_wa_author.contact_id → store de contactos) o, sin foto / sin contacto (otro número QR
// nuestro), las iniciales del nombre. Solo el avatar: la grilla sigue siendo la nativa de la derecha. null = nativo
// (eco 1:1 = Marcos desde su teléfono, sin autor; entrantes; actividad).
const OUTGOING = 1;
const teamAuthorOf = message => {
  if (typeOf(message) !== OUTGOING) return null;
  const attrs = message?.contentAttributes || message?.content_attributes;
  if (!isMasifiWaGroupMessage(attrs)) return null;
  const author = attrs.masifiWaAuthor || attrs.masifi_wa_author;
  return author?.team === true ? author : null;
};

export const masifiWaTeamContactId = message => {
  const author = teamAuthorOf(message);
  return author?.contactId ?? author?.contact_id ?? null;
};

export const masifiWaTeamEchoLayout = (message, contact) => {
  const author = teamAuthorOf(message);
  if (!author) return null;
  return {
    avatar: {
      name: contact?.name || author.name || '',
      src: contact?.thumbnail || '',
    },
  };
};

export const masifiWaLastActivityId = messages =>
  [...(messages || [])].reverse().find(message => typeOf(message) === ACTIVITY)
    ?.id;

// Lo que inserta el selector de menciones (ruling 7): "@Nombre " si el nombre es único entre los miembros cargados;
// si no, "@+<teléfono> " (o "@<lid> "), para que el backend no adivine.
export const masifiWaMentionText = (member, members = []) => {
  const sameName = members.filter(other => other.name === member.name);
  if (member.name && sameName.length <= 1 && !member.name.startsWith('+'))
    return `@${member.name} `;
  return member.phone ? `@+${member.phone} ` : `@${member.lid} `;
};

// Indicadores visuales de grupo (E2E fix batch 1, decisión de Marcos 2026-09-24). Solo presentación (ruling 23).
// Avatar de un grupo sin foto: el ícono de grupo en vez de las iniciales (con foto, la foto: lo decide el Avatar nativo).
export const MASIFI_WA_GROUP_ICON = 'i-lucide-users';
export const masifiWaGroupAvatarIcon = contact =>
  isMasifiWaGroupContact(contact) ? MASIFI_WA_GROUP_ICON : null;

// Autor del último mensaje en la vista previa de la lista ("Jvr: jajaja", como WhatsApp): solo mensajes de grupo con
// autor registrado; un compañero (team) se nombra como en la burbuja; nuestro agente (sin autor) → nativo.
export const masifiWaPreviewAuthor = message => {
  const attrs = message?.content_attributes || message?.contentAttributes;
  if (!isMasifiWaGroupMessage(attrs)) return null;
  const author = attrs.masifi_wa_author || attrs.masifiWaAuthor;
  if (!author) return null;
  const team = author.team === true;
  const name = team
    ? author.name
    : message?.sender?.name ||
      author.name ||
      (author.phone && `+${author.phone}`);
  return name ? { name, team } : null;
};

// Filtro EN VIVO de la lista (touchpoint: store/modules/conversations/helpers.js#applyPageFilters; revisión E2E fix 1,
// I3 + I1). El store inserta conversaciones que llegan por ActionCable y las filtra en el cliente; el servidor ya excluye
// esto en cada fetch (Groups::KindFilter y Gates::UnattendedScope), así que el cliente espeja lo mismo:
// - "Sin atender": un hilo de grupo abierto en silencio (join/importación) sin mensaje real todavía no cuenta.
// Solo presentación (ruling 23): el marcador del contacto-grupo; la marca del hilo sola no cambia un 1:1.
const SILENT_KEY = 'masifi_wa_group_joined';
const ACTIVE_KEY = 'masifi_wa_group_active_at';
const isDormantGroupChat = chat => {
  const attrs = chat?.additional_attributes || chat?.additionalAttributes || {};
  return (
    attrs[SILENT_KEY] === true &&
    !attrs[ACTIVE_KEY] &&
    isMasifiWaGroupChat(chat)
  );
};

// E2E fix batch 5 (item 6): también el tipo guardado ("Todas · Personas · Grupos", masifiWaSavedKind) para lo que llega
// en vivo (igual que en el servidor).
export const masifiWaPageFilter = (chat, conversationType, kind = 'all') => {
  if (conversationType === 'unattended' && isDormantGroupChat(chat))
    return false;
  if (kind === 'group') return isMasifiWaGroupChat(chat);
  if (kind === 'individual') return !isMasifiWaGroupChat(chat);
  return true;
};

// La forma del touchpoint (store/modules/conversations/helpers.js#applyPageFilters, import + 1 línea, E2E fix 8): los
// `filters` nativos traen `conversationType` y el tipo guardado `masifiWaKind` (ChatList#conversationFilters).
export const masifiWaApplyPageFilter = (chat, filters = {}) =>
  masifiWaPageFilter(chat, filters.conversationType, filters.masifiWaKind);

// ¿El módulo WhatsApp QR está entregado a esta cuenta? Mismo dato que el <Policy> del sidebar (account.features, que
// llena enabled_features con el gate efectivo entitlement + toggle del admin). `getters` = los del store raíz.
export const isMasifiWaQrEnabled = getters =>
  !!getters?.['accounts/isFeatureEnabledonAccount']?.(
    getters.getCurrentAccountId,
    WHATSAPP_QR_FEATURE
  );

// Tipo guardado por el usuario ("Todas · Personas · Grupos", ui_settings nativo — ruling 9). Solo cuenta con el
// módulo prendido: con él apagado el servidor lo ignora (Groups::KindFilter) y la lista también.
export const masifiWaSavedKind = (getters, uiSettings) => {
  if (!isMasifiWaQrEnabled(getters)) return 'all';
  const kind = uiSettings?.[MASIFI_WA_KIND_SETTING];
  return MASIFI_WA_KINDS.includes(kind) ? kind : 'all';
};

// "Silenciar" de un GRUPO (E2E fix 5, Marcos 2026-09-24). El mute nativo del menú de la conversación (MoreActions.vue)
// dice "Bloquear contacto" — en un grupo confunde. Solo en grupos: "Silenciar grupo" / "Reactivar grupo" (y sus
// avisos); el resto de las claves y todo 1:1 quedan nativos. Touchpoint: el `t` de MoreActions pasa por acá.
const GROUP_MUTE_KEYS = {
  'CONTACT_PANEL.MUTE_CONTACT': 'MASIFI_WHATSAPP_QR.GROUPS.MUTE.MUTE',
  'CONTACT_PANEL.UNMUTE_CONTACT': 'MASIFI_WHATSAPP_QR.GROUPS.MUTE.UNMUTE',
  'CONTACT_PANEL.MUTED_SUCCESS': 'MASIFI_WHATSAPP_QR.GROUPS.MUTE.MUTED',
  'CONTACT_PANEL.UNMUTED_SUCCESS': 'MASIFI_WHATSAPP_QR.GROUPS.MUTE.UNMUTED',
};
export const masifiWaGroupMuteI18n = (i18n, getChat) => ({
  ...i18n,
  t: (key, ...rest) =>
    i18n.t(
      (isMasifiWaGroupChat(getChat()) && GROUP_MUTE_KEYS[key]) || key,
      ...rest
    ),
});

// "Reactivar grupo" = desbloquear (nativo) + reabrir la conversación: el mute nativo la resolvió y desbloquear sola la
// deja resuelta (fricción del owner). EN ORDEN (E2E fix batch 6, m3): las acciones nativas se tragan el error de la API
// y solo commitean si salió bien, así que el resultado se lee del store: sin desbloqueo no se reabre (un hilo abierto
// con el contacto bloqueado no se reabre ni avisa) y va el aviso de error en vez de "Grupo reactivado"; si falla la
// reapertura, el aviso nativo de cambio de estado fallido. Touchpoint: los 2 unmute de MoreActions.vue
// (`if (…) return;`). 1:1 → null y el flujo nativo sigue igual. `alert` = useAlert nativo (inyectado: este helper lo
// importa el store y no debe arrastrar composables).
export const masifiWaGroupUnmute = (store, chat, t, alert) => {
  if (!isMasifiWaGroupChat(chat)) return null;
  const current = () => store.getters.getConversationById?.(chat.id) || chat;
  return (async () => {
    await store.dispatch('unmuteConversation', chat.id);
    if (current().muted) {
      alert(t('MASIFI_WHATSAPP_QR.GROUPS.MUTE.UNMUTE_FAILED'));
      return false;
    }
    await store.dispatch('toggleStatus', {
      conversationId: chat.id,
      status: 'open',
    });
    if (current().status !== 'open') {
      alert(t('CONVERSATION.CHANGE_STATUS_FAILED'));
      return false;
    }
    alert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
    return true;
  })();
};
